import { AuditTrailEntryDto } from './auditTrailTypes';

/** snake_case DB identifiers (table and column names) → "Start Case" display labels. */
export function formatAuditName(name: string): string {
    return name
        .split('_')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export function authorLabel(entry: Pick<AuditTrailEntryDto, 'author' | 'changedBy'>): string {
    return entry.author ?? entry.changedBy ?? '-';
}

export function authorInitials(entry: Pick<AuditTrailEntryDto, 'author' | 'changedBy' | 'authorId'>): string {
    if (entry.authorId === null) {
        return 'SYS';
    }
    return authorLabel(entry)
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');
}

export interface AvatarColors {
    background: string;
    text: string;
}

const SYSTEM_AVATAR: AvatarColors = { background: '#F0F0F0', text: '#9A9A9A' };

// Pastel pairs from the prototype; assignment is a stable hash of the author name.
const AVATAR_PALETTE: AvatarColors[] = [
    { background: '#E6F0E6', text: '#335131' },
    { background: '#EAE6F7', text: '#5B5BC7' },
    { background: '#FBEEE0', text: '#B76A00' },
    { background: '#E5EDF7', text: '#29295B' },
];

export function avatarColors(entry: Pick<AuditTrailEntryDto, 'author' | 'changedBy' | 'authorId'>): AvatarColors {
    if (entry.authorId === null) {
        return SYSTEM_AVATAR;
    }
    const name = authorLabel(entry);
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = (hash * 31 + name.charCodeAt(i)) | 0;
    }
    return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

/** The BE serializes changedAt as a UTC LocalDateTime without a zone suffix. */
export function parseChangedAt(changedAt: string): Date {
    return new Date(changedAt.endsWith('Z') ? changedAt : changedAt + 'Z');
}

// Audit timestamps are displayed in UTC (matching what the trigger wrote), never the browser zone —
// date-fns format()/isToday() would silently re-render them in local time.
const UTC_DATE_LABEL = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
});

const UTC_TIME_LABEL = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'UTC',
});

const DAY_MS = 24 * 60 * 60 * 1000;

function utcDayStamp(date: Date): number {
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/** The translated names of the two relative days; every other day is a UTC date label. */
export interface RelativeDayLabels {
    today: string;
    yesterday: string;
}

export function dateBucketLabel(date: Date, labels: RelativeDayLabels): string {
    const day = utcDayStamp(date);
    const today = utcDayStamp(new Date());
    if (day === today) {
        return labels.today;
    }
    if (day === today - DAY_MS) {
        return labels.yesterday;
    }
    return UTC_DATE_LABEL.format(date);
}

export function timeLabel(date: Date): string {
    return `${UTC_TIME_LABEL.format(date)} UTC`;
}

export interface DateBucket {
    label: string;
    entries: AuditTrailEntryDto[];
}

/** Groups consecutive entries (already sorted changedAt DESC by the BE) under date labels. */
export function bucketByDate(entries: AuditTrailEntryDto[], labels: RelativeDayLabels): DateBucket[] {
    const buckets: DateBucket[] = [];
    for (const entry of entries) {
        const label = dateBucketLabel(parseChangedAt(entry.changedAt), labels);
        const last = buckets[buckets.length - 1];
        if (last && last.label === label) {
            last.entries.push(entry);
        } else {
            buckets.push({ label, entries: [entry] });
        }
    }
    return buckets;
}

export type DiffKind = 'add' | 'remove' | 'update';

function isEmptyValue(value: unknown): boolean {
    return value === null || value === undefined || value === '';
}

export function diffKind(oldValue: unknown, newValue: unknown): DiffKind {
    const oldEmpty = isEmptyValue(oldValue);
    const newEmpty = isEmptyValue(newValue);
    if (oldEmpty && !newEmpty) {
        return 'add';
    }
    if (newEmpty && !oldEmpty) {
        return 'remove';
    }
    return 'update';
}

const TRUNCATE_AT = 46;

export interface FormattedAuditValue {
    /** Short text for the inline pill. */
    display: string;
    /** Full (pretty-printed for JSON) text for the preview dialog. */
    full: string;
    /** True when the full value needs the "view" dialog. */
    expandable: boolean;
}

function asJsonValue(value: unknown): unknown | undefined {
    if (typeof value === 'object' && value !== null) {
        return value;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
                const parsed: unknown = JSON.parse(trimmed);
                return typeof parsed === 'object' && parsed !== null ? parsed : undefined;
            } catch {
                return undefined;
            }
        }
    }
    return undefined;
}

export function formatAuditValue(value: unknown): FormattedAuditValue {
    const json = asJsonValue(value);
    if (json !== undefined) {
        return {
            display: Array.isArray(json) ? '[ … ]' : '{ … }',
            full: JSON.stringify(json, null, 2),
            expandable: true,
        };
    }
    const text = isEmptyValue(value) ? '' : String(value);
    if (text.length > TRUNCATE_AT) {
        return { display: text.slice(0, TRUNCATE_AT) + '…', full: text, expandable: true };
    }
    return { display: text, full: text, expandable: false };
}

/** `idLabel` is the translated "ID" prefix. */
export function rowIdLabel(rowId: string | null, idLabel: string): string {
    return rowId ? `${idLabel} ${rowId}` : '-';
}

export function shortTxId(transactionId: string): string {
    return `#${transactionId}`;
}
